import { ref, shallowRef } from "vue";
import type { FieldObservationType } from "@/types/field-observations.types";
import type { WorldBbox } from "@/utils/plan-analysis/types";

/**
 * État partagé du panneau Observations du canvas — permet d'ouvrir l'onglet
 * (et d'enchaîner sur une création) depuis un autre coin du canvas, ex. le
 * bouton « Créer une observation » du menu de sélection.
 *
 * Deux drapeaux consommés par deux composants distincts :
 * - `openRequested`   → CanvasDrawersMenus (ouvre le drawer Observations)
 * - `createRequested` → ObservationsMenu (ouvre l'éditeur en mode création,
 *   la sélection courante est capturée à ce moment-là)
 *
 * `activeTab` est l'onglet affiché dans le volet : `observations` (les
 * observations réellement créées), `ai` (les groupes détectés par l'analyse)
 * et `parts` (classement des observations par partie d'ouvrage). Tous ne
 * sont pas toujours proposés — cf. `availableDrawerTabs`.
 */
export const openRequested = ref(false);
export const createRequested = ref(false);

export type ObservationsDrawerTab = "ai" | "observations" | "parts";
export const activeTab = ref<ObservationsDrawerTab>("observations");

export function setActiveTab(tab: ObservationsDrawerTab): void {
  activeTab.value = tab;
}

/**
 * Onglet de l'analyse IA (groupes détectés) : le seul devant lequel les
 * cadres violets des groupes sont affichés sur le plan. L'onglet Parties
 * classe de vraies observations — il est du côté des cadres bleus.
 */
export function isAnalysisTab(tab: ObservationsDrawerTab): boolean {
  return tab === "ai";
}

/** Ce dont dépend la liste des onglets du volet. */
export interface DrawerTabsContext {
  /** Une analyse existe sur ce plan, sur ce poste. */
  hasAnalysis: boolean;
  /** Le plan est rattaché à un ouvrage (ou son lien est encore en lecture). */
  isLinked: boolean;
}

/**
 * Onglets proposés, dans l'ordre d'affichage : IA seulement s'il y a une
 * analyse ; Parties dès que le plan a un ouvrage (les parties sont les
 * siennes) — et toujours avec une analyse, pour que la barre ne change pas
 * de forme pendant qu'on y travaille. Un seul onglet = pas de barre.
 */
export function availableDrawerTabs(
  context: DrawerTabsContext,
): ObservationsDrawerTab[] {
  const tabs: ObservationsDrawerTab[] = [];
  if (context.hasAnalysis) tabs.push("ai");
  tabs.push("observations");
  if (context.hasAnalysis || context.isLinked) tabs.push("parts");
  return tabs;
}

/**
 * Onglet réellement affiché : l'onglet mémorisé s'il est proposé, sinon
 * Observations (analyse effacée, autre plan, plan sans ouvrage).
 */
export function resolveDrawerTab(
  tab: ObservationsDrawerTab,
  context: DrawerTabsContext,
): ObservationsDrawerTab {
  return availableDrawerTabs(context).includes(tab) ? tab : "observations";
}

/** Ouvre le drawer Observations et lance une création depuis la sélection. */
export function requestCreateObservation(): void {
  openRequested.value = true;
  createRequested.value = true;
}

export function consumeOpenRequest(): void {
  openRequested.value = false;
}

export function consumeCreateRequest(): void {
  createRequested.value = false;
}

// ── Liste des observations : filtre et tri ──────────────────────────────────
// Gardés ici, pas dans le composant de liste : passer par l'onglet IA ou
// Parties remonte le panneau, le filtre et le mode de tri choisis doivent y
// survivre.

/** date = liste plate ; part = sections au chemin complet ; tree = arborescence. */
export type ObservationsViewMode = "date" | "part" | "tree";
export const viewMode = ref<ObservationsViewMode>("date");

/** Types affichés — vide = tous. */
export const typeFilter = ref<Set<FieldObservationType>>(new Set());

export function toggleTypeFilter(type: FieldObservationType): void {
  const next = new Set(typeFilter.value);
  if (next.has(type)) next.delete(type);
  else next.add(type);
  typeFilter.value = next;
}

export function clearTypeFilter(): void {
  typeFilter.value = new Set();
}

// ── Cadres bleus des observations sur le plan ───────────────────────────────
// Les onglets Observations et Parties connaissent les observations et leurs
// entités ; la surcouche qui dessine les cadres vit dans le conteneur du
// canvas, hors du volet. Les deux se parlent par ici.

/** Cadre d'une observation, en coordonnées monde du plan. */
export interface ObservationFrameSource {
  /** `localId` de l'observation (clé du rattachement plan). */
  id: string;
  /** Lettre de l'observation, affichée sur le badge. */
  label: string;
  /** Emprise de ses entités sur le plan. */
  bbox: WorldBbox;
  /** Mise en avant : carte ouverte (Observations), ligne touchée (Parties). */
  isCurrent: boolean;
  /**
   * Mode association de l'onglet Parties : l'observation est-elle dans la
   * partie sélectionnée ? Null hors de ce mode.
   */
  assigned: boolean | null;
}

/** Fournis par l'onglet affiché (Observations ou Parties), vide sinon. */
export const observationFrameSources = shallowRef<ObservationFrameSource[]>([]);

/** Onglet qui fournit les cadres en ce moment. */
let framesOwner: symbol | null = null;

/** `owner` identifie l'onglet qui fournit : lui seul pourra les retirer. */
export function setObservationFrames(
  sources: ObservationFrameSource[],
  owner: symbol,
): void {
  framesOwner = owner;
  observationFrameSources.value = sources;
}

/**
 * Retire les cadres d'un onglet qui se ferme — sauf si un autre a déjà pris
 * le relais : passer d'Observations à Parties monte le second avant que le
 * premier n'ait fini de se démonter.
 */
export function releaseObservationFrames(owner: symbol): void {
  if (framesOwner !== owner) return;
  framesOwner = null;
  observationFrameSources.value = [];
}

/**
 * Geste fait sur les cadres du plan, à traiter par l'onglet affiché : un
 * badge touché (Observations : sa carte s'ouvre ; Parties : l'observation
 * est rattachée / détachée), ou une zone tracée en mode association (toutes
 * les observations qu'elle couvre sont rattachées).
 */
export interface ObservationFramePick {
  kind: "badge" | "zone";
  /** `localId` des observations visées. */
  ids: string[];
}

export const observationFramePick = shallowRef<ObservationFramePick | null>(
  null,
);

export function pickObservationFrames(pick: ObservationFramePick): void {
  observationFramePick.value = pick;
}

export function consumeObservationFramePick(): void {
  observationFramePick.value = null;
}

/** Nouveau plan ouvert : le volet repart de son état initial. */
export function resetObservationsPanel(): void {
  // Une création demandée sur le plan précédent ne démarre pas sur celui-ci.
  openRequested.value = false;
  createRequested.value = false;
  activeTab.value = "observations";
  viewMode.value = "date";
  typeFilter.value = new Set();
  framesOwner = null;
  observationFrameSources.value = [];
  observationFramePick.value = null;
}
